/**
 * Alert 元件
 *
 * 提供通知和警告訊息顯示：
 * - 支援多種類型（success, error, warning, info）
 * - 可選的關閉按鈕
 * - 自動消失功能
 */

import { escapeHtml } from '@/utils/security';

// ========================================================================
// 型別定義
// ========================================================================

export type AlertType = 'success' | 'error' | 'warning' | 'info';

export interface AlertOptions {
  /** 警告類型 */
  type?: AlertType;
  /** 標題（可選） */
  title?: string;
  /** 訊息內容（當使用物件方式建構時） */
  message?: string;
  /** 是否可關閉 */
  closable?: boolean;
  /** 自動關閉延遲（毫秒），0 表示不自動關閉 */
  autoClose?: number;
  /** 關閉時的回呼 */
  onClose?: () => void;
}

// ========================================================================
// 樣式映射
// ========================================================================

const TYPE_STYLES: Record<AlertType, { bg: string; border: string; text: string; icon: string }> = {
  success: {
    bg: 'bg-green-50',
    border: 'border-green-200',
    text: 'text-green-800',
    icon: `<svg class="w-5 h-5 text-green-400" fill="currentColor" viewBox="0 0 20 20">
      <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clip-rule="evenodd"/>
    </svg>`,
  },
  error: {
    bg: 'bg-red-50',
    border: 'border-red-200',
    text: 'text-red-800',
    icon: `<svg class="w-5 h-5 text-red-400" fill="currentColor" viewBox="0 0 20 20">
      <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clip-rule="evenodd"/>
    </svg>`,
  },
  warning: {
    bg: 'bg-yellow-50',
    border: 'border-yellow-200',
    text: 'text-yellow-800',
    icon: `<svg class="w-5 h-5 text-yellow-400" fill="currentColor" viewBox="0 0 20 20">
      <path fill-rule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clip-rule="evenodd"/>
    </svg>`,
  },
  info: {
    bg: 'bg-blue-50',
    border: 'border-blue-200',
    text: 'text-blue-800',
    icon: `<svg class="w-5 h-5 text-blue-400" fill="currentColor" viewBox="0 0 20 20">
      <path fill-rule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clip-rule="evenodd"/>
    </svg>`,
  },
};

// ========================================================================
// Toast 通知容器（內部使用）
// ========================================================================

let toastContainer: HTMLDivElement | null = null;

function getOrCreateToastContainer(): HTMLDivElement {
  if (!toastContainer) {
    toastContainer = document.createElement('div');
    toastContainer.className = 'fixed top-4 right-4 z-50 space-y-2 max-w-sm';
    document.body.appendChild(toastContainer);
  }
  return toastContainer;
}

// ========================================================================
// Alert 類別
// ========================================================================

export class Alert {
  private element: HTMLDivElement;
  private options: Required<Omit<AlertOptions, 'onClose' | 'message'>> & Pick<AlertOptions, 'onClose'>;
  private autoCloseTimer: number | null = null;
  private message: string;

  /**
   * 建立 Alert
   * @param messageOrOptions - 訊息字串或包含 message 的選項物件
   * @param options - 當第一個參數是字串時的選項物件
   */
  constructor(messageOrOptions: string | AlertOptions, options: AlertOptions = {}) {
    // 支援兩種建構方式：
    // 1. new Alert('message', { type: 'info' })
    // 2. new Alert({ message: 'message', type: 'info' })
    let resolvedMessage: string;
    let resolvedOptions: AlertOptions;

    if (typeof messageOrOptions === 'string') {
      resolvedMessage = messageOrOptions;
      resolvedOptions = options;
    } else {
      resolvedMessage = messageOrOptions.message ?? '';
      resolvedOptions = messageOrOptions;
    }

    this.message = resolvedMessage;
    this.options = {
      type: resolvedOptions.type ?? 'info',
      title: resolvedOptions.title ?? '',
      closable: resolvedOptions.closable ?? true,
      autoClose: resolvedOptions.autoClose ?? 0,
      onClose: resolvedOptions.onClose,
    };

    this.element = this.createElement(this.message);
    this.setupAutoClose();
  }

  // ========================================================================
  // 公開方法
  // ========================================================================

  getElement(): HTMLDivElement {
    return this.element;
  }

  mount(container: HTMLElement): void {
    container.appendChild(this.element);
  }

  /**
   * 顯示 Alert（自動掛載到 toast 容器）
   */
  show(): void {
    const container = getOrCreateToastContainer();
    this.mount(container);
  }

  close(): void {
    this.clearAutoClose();

    // 淡出動畫
    this.element.style.transition = 'opacity 200ms ease-out';
    this.element.style.opacity = '0';

    setTimeout(() => {
      this.element.remove();
      this.options.onClose?.();
    }, 200);
  }

  destroy(): void {
    this.clearAutoClose();
    this.element.remove();
  }

  // ========================================================================
  // 私有方法
  // ========================================================================

  private createElement(message: string): HTMLDivElement {
    const { type, title, closable } = this.options;
    const styles = TYPE_STYLES[type];

    // 🔒 安全性：轉義使用者輸入的 title 和 message 防止 XSS
    const safeTitle = title ? escapeHtml(title) : '';
    const safeMessage = escapeHtml(message);

    const element = document.createElement('div');
    element.className = `rounded-lg border p-4 ${styles.bg} ${styles.border}`;
    element.setAttribute('role', 'alert');

    element.innerHTML = `
      <div class="flex">
        <div class="flex-shrink-0">
          ${styles.icon}
        </div>
        <div class="ml-3 flex-1">
          ${safeTitle ? `<h3 class="text-sm font-medium ${styles.text}">${safeTitle}</h3>` : ''}
          <div class="text-sm ${styles.text} ${safeTitle ? 'mt-1' : ''}">${safeMessage}</div>
        </div>
        ${closable ? `
          <div class="ml-auto pl-3">
            <button type="button" class="inline-flex rounded-md p-1.5 ${styles.text} hover:bg-opacity-20 hover:bg-gray-900 focus:outline-none focus:ring-2 focus:ring-offset-2">
              <span class="sr-only">關閉</span>
              <svg class="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                <path fill-rule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clip-rule="evenodd"/>
              </svg>
            </button>
          </div>
        ` : ''}
      </div>
    `;

    // 綁定關閉按鈕事件
    if (closable) {
      const closeBtn = element.querySelector('button');
      closeBtn?.addEventListener('click', () => this.close());
    }

    return element;
  }

  private setupAutoClose(): void {
    if (this.options.autoClose > 0) {
      this.autoCloseTimer = window.setTimeout(() => {
        this.close();
      }, this.options.autoClose);
    }
  }

  private clearAutoClose(): void {
    if (this.autoCloseTimer !== null) {
      clearTimeout(this.autoCloseTimer);
      this.autoCloseTimer = null;
    }
  }
}

// ========================================================================
// 工廠函式
// ========================================================================

/**
 * 顯示成功訊息
 */
export function showSuccess(message: string, options?: Omit<AlertOptions, 'type'>): Alert {
  return new Alert(message, { ...options, type: 'success' });
}

/**
 * 顯示錯誤訊息
 */
export function showError(message: string, options?: Omit<AlertOptions, 'type'>): Alert {
  return new Alert(message, { ...options, type: 'error' });
}

/**
 * 顯示警告訊息
 */
export function showWarning(message: string, options?: Omit<AlertOptions, 'type'>): Alert {
  return new Alert(message, { ...options, type: 'warning' });
}

/**
 * 顯示資訊訊息
 */
export function showInfo(message: string, options?: Omit<AlertOptions, 'type'>): Alert {
  return new Alert(message, { ...options, type: 'info' });
}

/**
 * 顯示 Toast 通知
 */
export function toast(message: string, type: AlertType = 'info', duration: number = 3000): Alert {
  const alert = new Alert(message, {
    type,
    closable: true,
    autoClose: duration,
  });

  alert.mount(getOrCreateToastContainer());
  return alert;
}
